import moment from 'moment-timezone';
import { TableRows } from 'ui/tableRows';
import { Table } from 'ui/table';
import { ColumnData, TableHeader } from 'ui/tableHeader';
import { displayDateWithTodayAndYesterdayPrefix } from 'utils/date';
import { TipPlacement, Tooltip } from 'ui/tooltip';

import {
  StyledCellContainer,
  StyledCell,
  Email,
  CellSecondaryText,
  CellText,
} from './NodeVisitorsTakeover.style';
import {
  EnteredJourneyTooltipContent,
  LeavingTileTooltipContent,
} from './NodeVisitorsToolTipContent';
import { NODE_TYPES } from 'ui/workflows';
import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { NodeVisitorRow } from './NodeVisitors.types';

export const getUserProfileHref = (userId: string) => `/users/profiles/${userId}`;

const columnsMap = [
  { key: 'email', display: 'User' },
  { key: 'createdAt', display: 'Entered Journey' },
  { key: 'updatedAt', display: 'Entered tile' },
  { key: 'delayedUntil', display: 'Leaves tile' },
];

const getTableBodyColumns = (tableColumns: Record<string, any>[]): ColumnData[] => {
  return tableColumns.map((column: Record<string, any>) => {
    switch (column.key) {
      case 'email': {
        return {
          id: column?.key,
          name: column?.display,
          align: 'flex-start',
          value: 'primaryValue',
          RowCell: ({ row }: any) => {
            const { primaryValue, secondaryValue } = row;
            return (
              <StyledCell>
                <StyledCellContainer>
                  <Email
                    target="_blank"
                    href={getUserProfileHref(primaryValue)}
                    rel="noreferrer noopener"
                  >
                    {primaryValue}
                  </Email>
                  {secondaryValue && (
                    <CellSecondaryText>User ID {secondaryValue}</CellSecondaryText>
                  )}
                </StyledCellContainer>
              </StyledCell>
            );
          },
        };
      }
      case 'createdAt': {
        return {
          id: column?.key,
          name: column?.display,
          align: 'flex-start',
          value: column?.key,
          RowCell: ({ row }: any) => {
            const date = new Date(row[column.key]);
            return (
              <StyledCell>
                <Tooltip
                  placement={TipPlacement.Bottom}
                  isUnderlined
                  tooltipContent={<EnteredJourneyTooltipContent date={date} />}
                >
                  <CellText> {moment(date).fromNow()}</CellText>
                </Tooltip>
              </StyledCell>
            );
          },
        };
      }
      case 'updatedAt': {
        return {
          id: column?.key,
          name: column?.display,
          align: 'flex-start',
          value: column?.key,
          RowCell: ({ row }: { row: Record<string, any> }) => {
            const date = new Date(row[column.key]);
            return (
              <StyledCell>
                <StyledCellContainer>
                  <CellText>{displayDateWithTodayAndYesterdayPrefix(date)}</CellText>
                </StyledCellContainer>
              </StyledCell>
            );
          },
        };
      }
      case 'delayedUntil': {
        return {
          id: column?.key,
          name: column?.display,
          align: 'flex-start',
          value: column?.key,
          RowCell: ({ row }: any) => {
            const date = new Date(row[column.key]);
            return (
              <StyledCell>
                <Tooltip
                  placement={TipPlacement.Bottom}
                  isUnderlined
                  tooltipContent={<LeavingTileTooltipContent date={date} />}
                >
                  <CellText>{moment(date).fromNow()}</CellText>
                </Tooltip>
              </StyledCell>
            );
          },
        };
      }
      default: {
        return {
          id: column?.key,
          name: column?.display,
          align: 'flex-start',
          value: column?.key,
          RowCell: ({ row }: any) => {
            return (
              <StyledCell>
                <CellText>{row[column.key]}</CellText>{' '}
              </StyledCell>
            );
          },
        };
      }
    }
  });
};

export const NodeVisitorsTable = ({
  type,
  data,
  sortDirection,
  setSortDirection,
  sortBy,
  setSortBy,
}: {
  type: string;
  data: NodeVisitorRow[];
  sortDirection: string;
  setSortDirection: (nextSortDirection: SORT_DIRECTIONS) => void;
  sortBy: string;
  setSortBy: (nextSortBy: string) => void;
}) => {
  const isDelayOrHold = type === NODE_TYPES.DELAY || type === NODE_TYPES.HOLD;
  const columns = getTableBodyColumns(columnsMap);

  const filteredColumns = !!isDelayOrHold
    ? columns
    : getTableBodyColumns(columnsMap).filter((item) => item.value !== 'delayedUntil');
  return (
    <Table>
      <TableHeader
        columns={filteredColumns}
        setSortDirection={(nextSortDirection: string) => {
          setSortDirection(
            nextSortDirection as (typeof SORT_DIRECTIONS)[keyof typeof SORT_DIRECTIONS],
          );
        }}
        setSortedBy={setSortBy}
        sortDirection={sortDirection}
        sortedBy={sortBy}
      />
      <TableRows columns={filteredColumns} rows={data} />
    </Table>
  );
};
